/**
 * Per the ILMARCH-61 BE contract, `completionTracking` is a nullable string
 * union (currently a single member). Authoring it as a union of literals so
 * we keep the door open to additional tracking modes (e.g. "stages") without
 * a second type-rename pass.
 */
export type CompletionTrackingType = "percent_complete";

/**
 * Per-asset value for completion tracking, as returned in
 * statusCompletionsTracking keyed by status id. `value` is a percent in the
 * range 0..100 inclusive, matching the backend's StatusCompletionTrackingValue
 * contract (assets-api-server) — the wire value is not scaled.
 */
export type StatusCompletionValue = {
  entityType: CompletionTrackingType;
  value: number;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const clampPercent = (
  percent: number | null | undefined
): number | null => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) {
    return null;
  }
  return Math.round(clamp(percent, 0, 100));
};

export const getAssetPercentComplete = (
  asset: {
    statusId: string;
    statusCompletionsTracking?: Record<
      string,
      Pick<StatusCompletionValue, "value"> | null
    > | null;
  },
  statusId?: string
): number | null => {
  const targetStatusId = statusId ?? asset.statusId;
  const entry = asset.statusCompletionsTracking?.[targetStatusId];
  if (!entry) return null;
  return clampPercent(entry.value);
};

export const buildPercentCompleteUpdate = (
  statusId: string,
  percent: number | null
): {
  statusCompletionsTracking: Record<string, StatusCompletionValue | null>;
} => {
  const clamped = clampPercent(percent);
  return {
    statusCompletionsTracking: {
      [statusId]:
        clamped === null
          ? null
          : { entityType: "percent_complete", value: clamped },
    },
  };
};

export const isPercentCompleteStatus = (
  status:
    | { completionTracking?: CompletionTrackingType | null }
    | null
    | undefined
): boolean => status?.completionTracking === "percent_complete";
